<template>
    <div class="flex flex-column relative">
        <el-table
            :data="rows"
            @sort-change="sendOrder"
            style="width: 100%"
            border
            height="100%"
            :class="listingClasses"
        >
            <el-table-column
                v-if="mainColumn"
                fixed
                :sortable="mainColumn.sortable === true ? `custom` : false"
                :sort-method="sortColumn"
                :prop="mainColumn.name"
                :key="mainColumn.name"
                :label="mainColumn.label"
                show-overflow-tooltip
                :min-width="convertedWidth(mainColumn.width)"
            >
                <template #default="scope">
                    <div
                        class="subcell semibold"
                        v-html="
                            scope.row['formatted_' + mainColumn.name] != undefined
                                ? scope.row['formatted_' + mainColumn.name]
                                : scope.row[mainColumn.name]
                        "
                    ></div>
                </template>
            </el-table-column>
            <!-- NAME -->
            <el-table-column
                :fixed="index == 0"
                v-for="(header, index) in commonColumns"
                :key="`header-${header.name}-${header.rank}`"
                :prop="header.name"
                :label="header.label"
                :render-header="labelTooltiped"
                :sort-by="header.name"
                show-overflow-tooltip
                :sortable="header.sortable === true ? `custom` : false"
                :sort-method="sortColumn"
                :width="convertedWidth(header.width)"
            >
                <template #default="scope">
                    <template v-if="scope.row['formatted_' + header.name] != undefined">
                        <template
                            v-if="
                                typeof scope.row['formatted_' + header.name] == 'object' &&
                                scope.row['formatted_' + header.name].type == 'component'
                            "
                        >
                            <component
                                :key="`${header.name}_${scope.row.id}`"
                                :is="scope.row['formatted_' + header.name].name"
                                v-bind="scope.row['formatted_' + header.name].params"
                            ></component>
                        </template>
                        <div
                            v-else
                            v-html="scope.row['formatted_' + header.name]"
                            class="subcell"
                        />
                    </template>
                    <template v-else>{{ scope.row[header.name] }}</template>
                </template>
            </el-table-column>

            <el-table-column :label="$t.__('accounting.AgedBalanceTable.to_expire')">
                <el-table-column
                    v-for="header in amountToGoColumns"
                    :key="`header-${header.name}-${header.rank}`"
                    :prop="header.name"
                    :label="header.label"
                    :render-header="labelTooltiped"
                    :sort-by="header.name"
                    show-overflow-tooltip
                    :sortable="header.sortable === true ? `custom` : false"
                    :sort-method="sortColumn"
                    :width="convertedWidth(header.width)"
                >
                    <template #default="scope">
                        <template v-if="scope.row['formatted_' + header.name] != undefined">
                            <template
                                v-if="
                                    typeof scope.row['formatted_' + header.name] == 'object' &&
                                    scope.row['formatted_' + header.name].type == 'component'
                                "
                            >
                                <component
                                    :key="`${header.name}_${scope.row.id}`"
                                    :is="scope.row['formatted_' + header.name].name"
                                    v-bind="scope.row['formatted_' + header.name].params"
                                ></component>
                            </template>
                            <div
                                v-else
                                v-html="scope.row['formatted_' + header.name]"
                                class="subcell"
                            />
                        </template>
                        <template v-else>{{ scope.row[header.name] }}</template>
                    </template>
                </el-table-column>
            </el-table-column>
            <el-table-column :label="$t.__('accounting.AgedBalanceTable.expired')">
                <el-table-column
                    v-for="header in amountExpiredColumns"
                    :key="`header-${header.name}-${header.rank}`"
                    :prop="header.name"
                    :label="header.label"
                    :render-header="labelTooltiped"
                    :sort-by="header.name"
                    show-overflow-tooltip
                    :sortable="header.sortable === true ? `custom` : false"
                    :sort-method="sortColumn"
                    :width="convertedWidth(header.width)"
                >
                    <template #default="scope">
                        <template v-if="scope.row['formatted_' + header.name] != undefined">
                            <template
                                v-if="
                                    typeof scope.row['formatted_' + header.name] == 'object' &&
                                    scope.row['formatted_' + header.name].type == 'component'
                                "
                            >
                                <component
                                    :key="`${header.name}_${scope.row.id}`"
                                    :is="scope.row['formatted_' + header.name].name"
                                    v-bind="scope.row['formatted_' + header.name].params"
                                ></component>
                            </template>
                            <div
                                v-else
                                v-html="scope.row['formatted_' + header.name]"
                                class="subcell"
                            />
                        </template>
                        <template v-else>{{ scope.row[header.name] }}</template>
                    </template>
                </el-table-column>
            </el-table-column>

            <!-- ALL COLUMNS -->
        </el-table>
    </div>
</template>

<script>
import { Table, TableColumn } from 'element-ui';
import _ from 'lodash';
import ListSelector from 'vcomponents/listing/ListSelector.vue';

export default {
    props: {
        listinghash: {
            type: String,
            required: true,
        },
        rows: {
            type: Array,
            default() {
                return [];
            },
        },
        columns: {
            type: Array,
            default() {
                return [];
            },
        },
    },

    data() {
        return {
            width: {
                XS: 48,
                S: 96,
                M: 144,
                L: 176,
                XL: 196,
                XXL: 240,
            },
        };
    },

    computed: {
        amountToGoColumns() {
            return this.columns.filter((col) => /expected(.)*/gi.test(col.name));
        },
        commonColumns() {
            return this.columns.filter(
                (col) => /^(?!expected|late.*$).*/g.test(col.name) && !col.isMain,
            );
        },
        amountExpiredColumns() {
            return this.columns.filter((col) => /late(.)*/gi.test(col.name));
        },

        listingClasses() {
            let classes = '';

            if (!/^((?!chrome|android).)*safari/i.test(navigator.userAgent))
                classes += ' table-ellipsis';

            return classes;
        },

        displayedColumns() {
            return this.columns.filter((col) => !col.isMain);
        },

        mainColumn() {
            let columnIndex = _.findIndex(this.columns, { isMain: true });

            if (columnIndex != -1) {
                return this.columns[columnIndex];
            }

            return false;
        },

        display() {
            return this.$store.state[this.listinghash].display;
        },

        sorting() {
            return this.$store.state[this.listinghash].sorting;
        },

        tableDensity() {
            return this.$store.state[this.listinghash].display.density;
        },
    },

    created() {
        for (var i in this.rows) {
            this.rows[i].formatted_address =
                (this.rows[i].address_zip ? this.rows[i].address_zip : '') +
                ' ' +
                (this.rows[i].address_town ? this.rows[i].address_town : '');
        }
    },

    methods: {
        labelTooltiped(h, { column }) {
            return h('el-tooltip', { props: { content: column.label, placement: 'top' } }, [
                h('span', {}, column.label),
            ]);
        },

        convertedWidth(value) {
            return this.width[value];
        },

        sortBy(column) {
            let newSorting = {
                field: column,
                direction: 'asc',
            };

            if (this.sorting.field == column && this.sorting.direction == 'asc') {
                newSorting.direction = 'desc';
            }

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        },

        sendOrder: _.debounce(function (column) {
            let newOrder = column.order == 'descending' ? 'desc' : 'asc';
            let newSorting = {
                field: column.prop,
                direction: newOrder,
            };

            this.$store.commit(`${this.listinghash}/updateSorting`, newSorting);
            this.$store.dispatch(`${this.listinghash}/fetch`);
        }, 300),

        goto(url, target) {
            window.open(url, target);
        },

        sortColumn(a, b) {
            return 0;
        },
    },

    components: {
        ListSelector,
    },
};
</script>
